import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';

import { getQueryParam } from '../helpers';

import '@awesome.me/webawesome/dist/components/input/input.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';

import {
  APIlogin,
  APIsetupAuth,
} from '../api/Auth';

import Loading from '../components/Loading';

interface LoginFormData {
  email: string;
  password: string;
};

const Login: React.FC = () => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState<boolean>(false);
  const { register, reset, handleSubmit } = useForm<LoginFormData>();

  const { mutate, isPending, isError } = useMutation({
    mutationFn: async (data: LoginFormData) => {
      await APIsetupAuth();
      const response = await APIlogin(data.email, data.password);
      return response;
    },
    onError: (error) => {
      setErrorMessage(error.message || 'An error occurred during login.');
      reset({ password: '' });
    },
    onSuccess: () => {
      setErrorMessage(null);
      setRedirecting(true);
      if (window.location.search) {
        const redirectUrl = getQueryParam('redirect');
        if (redirectUrl) {
          window.location.href = redirectUrl;
          return;
        }
      }
      window.location.href = '/dashboard';
    },
  });

  const onSubmit = (data: LoginFormData) => {
    mutate({ email: data.email, password: data.password });
  };

  if (redirecting) {
    return (
      <Loading />
    )
  }

  return (
    <div className="container login-container">
      <div className="wa-grid wa-gap-0">
        <div className="login-form">
          {isError && (
            <wa-callout variant="danger">
              <i className="fa-regular fa-circle-exclamation" slot="icon"></i>
              <strong>{errorMessage}</strong>
            </wa-callout>
          )}
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <wa-input type="email" label="E-mail" required autofocus {...register('email')}></wa-input>
            <wa-input type="password" label="Password" required {...register('password')} password-toggle>
              <div slot="hint">
                <Link to="/password/reset">Forgot password?</Link>
              </div>
            </wa-input>
            <wa-button type="submit" disabled={isPending}>
              {isPending ? 'Logging in...' : 'Login'}
            </wa-button>
          </form>
          <div className="login-info">
            Don't have an account? <Link to="/signup">Sign up here</Link>.
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
